import { useModal } from '@ebay/nice-modal-react'
import { Link, useMatch, useNavigate } from '@tanstack/router'
import axios from 'axios'
import { useEffect, useState } from 'react'
import React from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'

import coin_anim from '../../assets/common/coin_anim.gif'
import show_profile from '../../assets/common/show_profile.svg'
import show_profile_light from '../../assets/common/show_profile_light.svg'
import useGeoCheck from '../../hooks/useGeoCheck'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { UtmParams } from '../../types/common'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import ProfileDrawer from '../profile/profile-drawer'
import LoginModal from './log-in-modal'

interface HeaderProps {
  hideBalance?: boolean
}

const Header = ({ hideBalance }: HeaderProps) => {
  const match = useMatch().params as { affiliate?: string } & UtmParams
  const { geoCheck } = useGeoCheck()
  const navigate = useNavigate()
  const theme = useBoundStore(state => state.common.theme)
  const isCoinAnimVisible = useBoundStore(
    state => state.common.isCoinAnimVisible,
  )
  const unified_balance = useBoundStore(state => state.user.unified_balance)
  const username = useBoundStore(state => state.user.username)
  const logInModal = useModal(LoginModal)
  const profileDrawerModal = useModal(ProfileDrawer)
  const { t } = useTranslation('common')
  const { refreshBalance, isLoading: balanceLoading } = useRefreshBalance()
  const [noOfUnread, setNoOfUnread] = useState(0)
  const [affiliateId, setAffiliateId] = useState('')

  useEffect(() => {
    if (!match.affiliate) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchAffiliateId = async () => {
      try {
        const response = await api.post<ApiResponse<string>>(
          '/ajax_datav2.php',
          {
            type: 'get_agent_id',
            affiliate: match.affiliate,
          },
          { signal },
        )
        const { info, status } = response.data
        if (status === 1) {
          setAffiliateId(info)
        } else {
          navigate({ to: '/' })
        }
      } catch (error) {
        navigate({ to: '/' })
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchAffiliateId()

    return () => {
      controller.abort()
    }
  }, [username])

  useEffect(() => {
    if (!username) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchNumberOfUnreadMessages = async () => {
      try {
        const response = await api.post<ApiResponse<string>>(
          '/ajax_data.php',
          {
            type: 'noread_message',
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1) {
          setNoOfUnread(parseInt(info))
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchNumberOfUnreadMessages()

    return () => {
      controller.abort()
    }
  }, [username])

  useEffect(() => {
    if (!username) return
    refreshBalance()
  }, [username])

  return (
    <div className="flx-btw-ctr sticky z-20 bg-primary h-[60px] shadow-[0px_1px_5px_rgba(0,0,0,0.1)] pl-0 pr-5 py-2.5 left-0 top-0">
      <div className="flx-ctr">
        <div style={{ position: 'relative', display: 'flex' }}>
          {noOfUnread > 0 && (
            <p className="flx-ctr absolute w-[7px] h-[7px] bg-[#eb4243] rounded-md left-[35px] top-[17px]" />
          )}
          <img
            className="w-[60px] aspect-square p-5"
            src={theme === 'dark' ? show_profile : show_profile_light}
            alt="Show Profile"
            onClick={() => profileDrawerModal.show()}
          />
        </div>
        <Link to="/" className="flx-ctr">
          <img
            src={`https://www.u2d8899.com/mexicoimages/logo/${theme}.png`}
            alt="Company Logo"
            style={{ width: '78px', aspectRatio: '177 / 80' }}
          />
        </Link>
      </div>
      <div className="flx-ctr">
        {username ? (
          <>
            {!hideBalance && (
              <>
                <button
                  className="flx-ctr"
                  onClick={() => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'deposit_form_open',
                        event_category: 'deposit_funds',
                        event_action: 'click_open',
                        event_label: 'Deposit form Open',
                        user_id: username,
                      },
                    })
                    window.open(`${location.origin}/deposit`, 'blank')
                  }}>
                  <div className="relative mr-2 -mt-[5px]">
                    <Icon
                      className="fill-txt"
                      id="wallet"
                      width="22"
                      height="23"
                    />
                    {isCoinAnimVisible && (
                      <img
                        className="absolute top-[-5px] left-[-60px] w-[150px] animate-fadeIn pointer-events-none"
                        src={coin_anim}
                      />
                    )}
                  </div>
                  <span className="text-sm text-txt mr-2">
                    ${numberWithCommas(parseFloat(unified_balance))}
                  </span>
                </button>
                <button
                  onClick={refreshBalance}
                  className={classNames(
                    'flex',
                    balanceLoading && 'animate-rotate2',
                  )}>
                  <Icon
                    id="refresh-balance"
                    width="17"
                    height="17"
                    fill="#D2D4D6"
                  />
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <div
              className="flex items-center font-Syncopate-Bold text-[10px] text-txt transition-all duration-[0.2s] ease-[ease-in-out] uppercase mr-2.5"
              onClick={() => geoCheck(logInModal.show)}>
              <Icon
                className="fill-txt mr-2"
                id="login"
                width="16"
                height="15"
              />
              {t('login')}
            </div>
            <button
              onClick={() =>
                geoCheck(() =>
                  navigate({
                    to: '/register',
                    search: { ...match, referral: affiliateId },
                  }),
                )
              }>
              <span className="flx-ctr h-[35px] bg-[#df0033] px-[9px] py-0 rounded-sm">
                <p className="text-[10px] text-white uppercase font-Syncopate-Bold">
                  {t('register')}
                </p>
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default Header
